'use client';

import { filterBlank } from '@dte/otw-utils-core/src/lists/filterEmpty';
import { isEmptyArray } from '@dte/otw-utils-core/src/objects/isEmpty';
import { FullStory } from '@fullstory/browser';
import { type ReactNode, useEffect, useMemo } from 'react';
import { isProd } from '../../common/util/environmentConfig';
import { useMsalAccount } from '../../contexts/TokenContext/useMsalAccount';
import { useRoles } from '../../contexts/TokenContext/useRoles';
import type { FullStoryIdentity, FullStoryUserProperties } from './FullStoryIdentity';
import { useFullStoryReady } from './useFullStoryReady';

function useFullStoryIdentity(): FullStoryIdentity {
	// Get the account from MSAL
	const account = useMsalAccount();

	// Keep track of the parts of the account info that we need to send to FullStory
	const identity = useMemo(() => {
		const uid = account?.homeAccountId;
		if (!uid) {
			return undefined;
		}

		const email = account.username;
		if (!email) {
			return undefined;
		}
		const displayName = account.name;

		const newIdentity: FullStoryIdentity = {
			anonymous: false,
			uid: uid,
			properties: {
				displayName: displayName,
				email: email,
			},
		};
		return newIdentity;
	}, [account]);

	return identity;
}

function useLogUserRoles(): void {
	const [fullStoryReady] = useFullStoryReady();

	const roles = useRoles();

	// Stable / sorted roles list
	const rolesList = useMemo(() => {
		const newRoles = filterBlank(roles);
		if (isEmptyArray(newRoles)) {
			return undefined;
		}
		newRoles.sort();
		return newRoles.join(',');
	}, [roles]);

	const roleProperties = useMemo(() => {
		if (!rolesList) {
			return undefined;
		}

		const properties: FullStoryUserProperties = {
			type: 'user',
			properties: {
				roles: rolesList,
			},
		};

		return properties;
	}, [rolesList]);

	// If the parts we care about change, send that fo FullStory
	useEffect(() => {
		// No data
		if (!roleProperties) {
			return;
		}
		if (!fullStoryReady) {
			return;
		}

		if (!isProd) {
			console.log('Logging user roles to FullStory: ', roleProperties.properties.roles);
		}

		// Log user properties to FullStory
		FullStory('setProperties', roleProperties);
	}, [roleProperties, fullStoryReady]);
}

function useLogUserIdentity(): void {
	// Keep track of the parts of the account info that we need to send to FullStory
	const identity = useFullStoryIdentity();
	const [fullStoryReady] = useFullStoryReady();

	// If the parts we care about change, send that fo FullStory
	useEffect(() => {
		// No data
		if (!identity) {
			return;
		}
		if (!fullStoryReady) {
			return;
		}

		if (!isProd) {
			console.log('Logging user to FullStory: ', identity.properties.email);
		}

		// Log user information to FullStory
		FullStory('setIdentity', identity);
	}, [identity, fullStoryReady]);
}

export function FullStoryUserLogger(): ReactNode {
	useLogUserIdentity();
	useLogUserRoles();

	return null;
}
